import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { Page, Header, Content, Progress } from '@backstage/core-components';
import {
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Divider,
  Box,
  Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  getSurfaceBorder,
  getSurfaceColor,
  getThemeTokens,
} from '../../theme/tokens';

type ConsentSession = {
  id: string;
  clientName?: string;
  scope?: string;
  redirectUri: string;
};

export const OAuthConsentPage = () => {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);
  const { sessionId } = useParams<{ sessionId: string }>();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [session, setSession] = useState<ConsentSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('auth');
        const response = await fetchApi.fetch(
          `${baseUrl}/v1/sessions/${sessionId}`,
        );
        if (!response.ok) {
          const body = await response.json();
          throw new Error(body.error_description || 'Session not found');
        }
        setSession(await response.json());
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load session');
      } finally {
        setLoading(false);
      }
    };
    fetchSession();
  }, [sessionId, discoveryApi, fetchApi]);

  const handleAction = async (action: 'approve' | 'reject') => {
    setSubmitting(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('auth');
      const response = await fetchApi.fetch(
        `${baseUrl}/v1/sessions/${sessionId}/${action}`,
        { method: 'POST' },
      );
      if (!response.ok) {
        const body = await response.json();
        throw new Error(body.error_description || `Failed to ${action}`);
      }
      const { redirectUrl } = await response.json();
      window.location.href = redirectUrl;
    } catch (e) {
      setError(e instanceof Error ? e.message : `Failed to ${action}`);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Page themeId="tool">
        <Header title="Authorization" />
        <Content>
          <Progress />
        </Content>
      </Page>
    );
  }

  if (error || !session) {
    return (
      <Page themeId="tool">
        <Header title="Authorization" />
        <Content>
          <Box display="flex" justifyContent="center" mt={4}>
            <Card
              sx={{
                maxWidth: 520,
                width: '100%',
              }}
            >
              <CardContent>
                <Typography variant="h6" color="error">
                  Authorization Error
                </Typography>
                <Typography variant="body1" sx={{ marginTop: 1 }}>
                  {error || 'Session not found'}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Content>
      </Page>
    );
  }

  const scopes = session.scope?.split(' ').filter(Boolean) ?? [];

  return (
    <Page themeId="tool">
      <Header
        title="Authorization Request"
        subtitle="Review the scopes before granting access to your Backstage account."
      />
      <Content>
        <Box display="flex" justifyContent="center" mt={4}>
          <Card
            sx={{
              backgroundColor: getSurfaceColor(theme, 'raised'),
              border: `1px solid ${getSurfaceBorder(theme)}`,
              maxWidth: 540,
              overflow: 'hidden',
              width: '100%',
            }}
          >
            <Box
              sx={{
                background: `linear-gradient(135deg, ${getSurfaceColor(
                  theme,
                  'subtle',
                )} 0%, ${getSurfaceColor(theme, 'accent')} 100%)`,
                borderBottom: `1px solid ${getSurfaceBorder(theme)}`,
                px: 3,
                py: 2.5,
              }}
            >
              <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                OAUTH CONSENT
              </Typography>
            </Box>
            <CardContent>
              <Typography variant="h6">
                {session.clientName || 'An application'} is requesting access
              </Typography>
              <Divider sx={{ margin: '16px 0' }} />
              {scopes.length > 0 && (
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Requested scopes
                  </Typography>
                  <Box display="flex" flexWrap="wrap" sx={{ gap: 1 }}>
                    {scopes.map(scope => (
                      <Chip key={scope} label={scope} size="small" />
                    ))}
                  </Box>
                </Box>
              )}
              <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
                This will allow the application to act on your behalf through
                the Backstage API.
              </Typography>
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end', padding: 2 }}>
              <Button
                onClick={() => handleAction('reject')}
                disabled={submitting}
              >
                Deny
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleAction('approve')}
                disabled={submitting}
              >
                {submitting ? 'Authorizing...' : 'Authorize'}
              </Button>
            </CardActions>
          </Card>
        </Box>
      </Content>
    </Page>
  );
};
